import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo } from "react"
import { XMarkIcon, Bars3Icon, ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import Spinner from "aldoo-ra/Spinner"
import Tooltip from "aldoo-ra/Tooltip"
import { useFieldDragController } from "../controllers/field-drag-controller"
import { AIRewriteLocalization } from "aldoo-ra/Localization/api"
import LocalizationAIRewriteDialog from "aldoo-ra/Localization/localization-ai-rewrite-dialog"
import { showNotification } from "aldoo-ra/Notification"
import _ from "lodash"

const RobotIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h3a3 3 0 0 1 3 3v1h.5a1.5 1.5 0 0 1 0 3H19v1a3 3 0 0 1-3 3H8a3 3 0 0 1-3-3v-1h-.5a1.5 1.5 0 0 1 0-3H5v-1a3 3 0 0 1 3-3h3V5.73A2 2 0 0 1 10 4a2 2 0 0 1 2-2zm-2 9a1.5 1.5 0 0 0 0 3 1.5 1.5 0 0 0 0-3zm4 0a1.5 1.5 0 0 0 0 3 1.5 1.5 0 0 0 0-3zm-5 5v1h6v-1H9z"/>
  </svg>
)

const ObjectEditor = forwardRef(({
  onChange,
  value,
  field,
  readOnly = false,
  showInfo = true,
  parentObject,
  showClearBtn = true,
  showAddBtn = true,
  showItemDelete = true,
  onClearRef,
}, ref) => {
  const getInitialValue = () => {
    if (Array.isArray(value) && value.length > 0) return value
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      // Convert object to array format
      return Object.entries(value).map(([key, val]) => ({ key, value: val }))
    }
    // Always start with one empty item for easier use
    return [{ key: "", value: "" }]
  }

  const [localValue, setLocalValue] = useState(getInitialValue)
  const [search, setSearch] = useState("")
  const inputRefs = useRef([])

  // Save-time formatting collapses duplicates via Object.fromEntries-style
  // reduce: last-write-wins silently overwrites earlier values. Flag dupes
  // in the UI so the user notices.
  const dupKeySet = useMemo(() => {
    const counts = new Map()
    for (const item of localValue || []) {
      const k = item?.key
      if (typeof k !== "string" || !k.length) continue
      counts.set(k, (counts.get(k) || 0) + 1)
    }
    const dups = new Set()
    for (const [k, n] of counts) if (n > 1) dups.add(k)
    return dups
  }, [localValue])
  const isInternalChange = useRef(false)
  const [aiRewriteIndex, setAiRewriteIndex] = useState(null) // index of row open in AI rewrite dialog
  const [rewriteAllOpen, setRewriteAllOpen] = useState(false)
  const [rewriteAllDirections, setRewriteAllDirections] = useState("")
  const [rewriteAllLoading, setRewriteAllLoading] = useState(false)

  const isRequired =
    typeof field.required === "function"
      ? field.required(parentObject)
      : field.required

  // Unified function to update value and notify parent
  const updateValue = (newValue) => {
    isInternalChange.current = true
    setLocalValue(newValue)
    onChange?.(newValue)
  }

  // Drag controller support - moved after updateValue definition
  const { handleDragStart, handleDragOver, handleDragEnd } =
    useFieldDragController(localValue, updateValue)

  // Handle external value changes
  useEffect(() => {
    if (!isInternalChange.current && !_.isEqual(value, localValue)) {
      setLocalValue(getInitialValue())
    }
    isInternalChange.current = false
  }, [value])

  const handleValueChange = (key, updatedValue, index) => {
    try {
      const updatedValues = [...localValue]
      if (!updatedValues[index]) {
        updatedValues[index] = {}
      }
      updatedValues[index] = {
        ...updatedValues[index],
        [key]: updatedValue,
      }
      updateValue(updatedValues)
    } catch (error) {
    }
  }

  const addNewInput = () => {
    if (readOnly) return

    const newValues = [...localValue, { key: "", value: "" }]
    updateValue(newValues)

    setTimeout(() => {
      const lastInputIndex = localValue.length
      inputRefs.current[lastInputIndex]?.key?.focus()
    }, 0)
  }

  const addNewInputWithScroll = () => {
    if (readOnly) return

    const newValues = [...localValue, { key: "", value: "" }]
    updateValue(newValues)

    setTimeout(() => {
      const lastInputIndex = localValue.length
      inputRefs.current[lastInputIndex]?.key?.focus()
      
      // Scroll to bottom of the scrollable container in the parent
      const scrollableContainer = document.querySelector('.max-h-\\[calc\\(100vh-300px\\)\\]')
      if (scrollableContainer) {
        scrollableContainer.scrollTop = scrollableContainer.scrollHeight
      }
    }, 100) // Increased timeout to allow DOM to update
  }

  const removeInput = (index) => {
    if (readOnly) return
    const updatedValues = localValue.filter((_, i) => i !== index)
    updateValue(updatedValues)
  }

  const clearAll = () => {
    if (readOnly) return
    updateValue([])
  }

  // Expose clear function to parent component
  useEffect(() => {
    if (onClearRef && typeof onClearRef === 'function') {
      onClearRef(clearAll)
    }
  }, [onClearRef])

  // Expose functions to parent via ref
  useImperativeHandle(ref, () => ({
    addItem: addNewInputWithScroll,
    clearAll: clearAll
  }), [])

  const handleBlur = (e, field, index) => {
    const trimmed = e.target.value.trim()
    if (trimmed !== e.target.value) {
      handleValueChange(field, trimmed, index)
    }
  }

  const handleKeyDown = (e, field, index) => {
    if (readOnly) return
    if (e.key === "Enter") {
      e.preventDefault()
      const trimmed = e.target.value.trim()
      if (trimmed !== e.target.value) {
        handleValueChange(field, trimmed, index)
      }
      addNewInput()
    }
  }

  const renderItem = (item, index) => {
    // Safety check for item
    if (!item) return null

    const isDup = !!item.key && dupKeySet.has(item.key)

    return (
      <div
        key={index}
        className={`w-full flex flex-row items-center gap-2 mt-2 ${isDup ? "rounded-md ring-1 ring-admin_error/60 px-1 py-1 -mx-1" : ""}`}
        draggable={!readOnly && localValue.length > 1}
        onDragStart={!readOnly && localValue.length > 1 ? () => handleDragStart(index) : undefined}
        onDragOver={!readOnly && localValue.length > 1 ? (e) => {
          e.preventDefault()
          handleDragOver(index)
        } : undefined}
        onDragEnd={!readOnly && localValue.length > 1 ? handleDragEnd : undefined}
      >
        {!readOnly && localValue.length > 1 && (
          <Bars3Icon className="w-4 h-4 text-admin_text_secondary flex-shrink-0 cursor-move" />
        )}
        <div className="w-1/3 relative">
          <Input
            variant="admin"
            readOnly={readOnly}
            ref={(el) =>
              (inputRefs.current[index] = {
                ...inputRefs.current[index],
                key: el,
              })
            }
            className={`w-full text-admin_text ${isDup ? "!border-admin_error" : ""}`}
            value={item.key || ""}
            placeholder="Key"
            onChange={(e) => handleValueChange("key", e.target.value, index)}
            onBlur={(e) => handleBlur(e, "key", index)}
            onKeyDown={(e) => handleKeyDown(e, "key", index)}
          />
        </div>

        {isDup && (
          <Tooltip content="Only the LAST entry's value will be saved" position="top">
            <span className="inline-flex items-center gap-1 px-2 h-7 rounded-md bg-admin_error/10 text-admin_error text-xs font-medium whitespace-nowrap flex-shrink-0">
              <ExclamationTriangleIcon className="w-4 h-4" />
              Duplicate key
            </span>
          </Tooltip>
        )}

        <div className="flex-grow">
          <Input
            variant="admin"
            readOnly={readOnly}
            ref={(el) =>
              (inputRefs.current[index] = {
                ...inputRefs.current[index],
                value: el,
              })
            }
            className="w-full text-admin_text"
            value={item.value || ""}
            placeholder="Value"
            onChange={(e) => handleValueChange("value", e.target.value, index)}
            onBlur={(e) => handleBlur(e, "value", index)}
            onKeyDown={(e) => handleKeyDown(e, "value", index)}
          />
        </div>

        {!readOnly && (
          <Tooltip content="AI rewrite this value" position="top">
            <button
              onClick={() => setAiRewriteIndex(index)}
              className="w-8 h-8 rounded-full border border-admin_bordered flex items-center justify-center text-admin_primary hover:border-admin_primary transition-colors flex-shrink-0"
            >
              <RobotIcon className="w-4 h-4" />
            </button>
          </Tooltip>
        )}

        {!readOnly && showItemDelete && (
          <Button
            variant="admin_icon"
            className="text-admin_text hover:bg-admin_error hover:text-white rounded-full p-2 transition-colors"
            onClick={() => removeInput(index)}
            title="Remove this item"
          >
            <XMarkIcon className="w-5 h-5" />
          </Button>
        )}
      </div>
    )
  }

  const handleRewriteAll = async () => {
    if (!rewriteAllDirections.trim()) return
    setRewriteAllLoading(true)
    try {
      const updated = [...localValue]
      for (let i = 0; i < updated.length; i++) {
        const item = updated[i]
        if (!item?.value?.trim()) continue
        const res = await AIRewriteLocalization({ text: item.value, directions: rewriteAllDirections })
        if (res?.result) updated[i] = { ...item, value: res.result }
      }
      updateValue(updated)
      setRewriteAllOpen(false)
      setRewriteAllDirections("")
      showNotification({ message: "All values rewritten", className: "bg-admin_primary text-admin_text_inv" })
    } catch {
      showNotification({ message: "AI rewrite failed", className: "bg-admin_error text-white" })
    } finally {
      setRewriteAllLoading(false)
    }
  }

  return (
    <div className="object-editor">
      {/* Per-key AI rewrite dialog */}
      {aiRewriteIndex !== null && (
        <LocalizationAIRewriteDialog
          originalText={localValue[aiRewriteIndex]?.value || ""}
          onAccept={(rewritten) => {
            updateValue(localValue.map((item, i) =>
              i === aiRewriteIndex ? { ...item, value: rewritten } : item
            ))
          }}
          onClose={() => setAiRewriteIndex(null)}
        />
      )}

      <div className="flex items-center justify-between gap-3 ml-10 mr-2 mt-1">
        <div className="flex items-center gap-3">
          <span className="text-sm text-admin_text">
            {localValue.length} {localValue.length === 1 ? "item" : "items"}
          </span>
          {dupKeySet.size > 0 && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-admin_error">
              <ExclamationTriangleIcon className="w-4 h-4" />
              {dupKeySet.size} duplicate {dupKeySet.size === 1 ? "key" : "keys"}
            </span>
          )}
        </div>
        {localValue.length > 5 && (
          <div className="relative w-72 max-w-full">
            <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-admin_text_secondary pointer-events-none" viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by key or value…"
              className="w-full pl-8 pr-2 py-1.5 text-sm rounded-md border border-admin_bordered bg-admin_paper text-admin_text placeholder-admin_text_secondary focus:outline-none focus:ring-1 focus:ring-admin_primary"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded text-admin_text_secondary hover:text-admin_text"
                title="Clear filter"
              >
                <XMarkIcon className="w-4 h-4 mx-auto" />
              </button>
            )}
          </div>
        )}
      </div>

      {Array.isArray(localValue) && (() => {
        const q = search.trim().toLowerCase()
        const rendered = []
        let matchCount = 0
        for (let index = 0; index < localValue.length; index++) {
          const item = localValue[index]
          if (q) {
            const k = String(item?.key || "").toLowerCase()
            const v = String(item?.value || "").toLowerCase()
            if (!k.includes(q) && !v.includes(q)) continue
          }
          matchCount++
          rendered.push(renderItem(item, index))
        }
        if (q && matchCount === 0) {
          rendered.push(
            <p key="no-match" className="ml-10 mt-3 text-sm text-admin_text_secondary">
              No items match &quot;{search}&quot;.
            </p>
          )
        }
        return rendered
      })()}

      <div className="flex flex-row justify-end items-center mt-4">
        <div className="flex items-center space-x-2">
          {!readOnly && showAddBtn && (
            <Button
              variant="admin_secondary"
              onClick={addNewInput}
            >
              Add
            </Button>
          )}

          {!readOnly && showClearBtn && localValue.length > 0 && (
            <Button
              variant="admin_secondary"
              onClick={clearAll}
            >
              Clear
            </Button>
          )}

          {/* Rewrite All button */}
          {!readOnly && localValue.length > 0 && (
            <div className="relative">
              <Tooltip content="AI rewrite all values" position="top">
                <button
                  onClick={() => { setRewriteAllOpen(v => !v); setRewriteAllDirections("") }}
                  className="w-8 h-8 rounded-full border border-admin_bordered flex items-center justify-center text-admin_primary hover:border-admin_primary transition-colors"
                >
                  <RobotIcon className="w-4 h-4" />
                </button>
              </Tooltip>
              {rewriteAllOpen && (
                <div className="absolute right-0 bottom-full mb-2 z-[500] bg-admin_paper border border-admin_bordered rounded-lg shadow-xl p-3 flex flex-col gap-2 w-72">
                  <p className="text-xs text-admin_text_secondary">Enter directions — all values will be rewritten using AI.</p>
                  <textarea
                    autoFocus
                    value={rewriteAllDirections}
                    onChange={(e) => setRewriteAllDirections(e.target.value)}
                    rows={3}
                    className="w-full px-2 py-1.5 text-sm border border-admin_bordered rounded focus:outline-none focus:ring-1 focus:ring-admin_primary bg-admin_paper text-admin_text resize-none"
                    placeholder="e.g. Make it more formal"
                  />
                  <div className="flex gap-2 justify-end items-center">
                    {rewriteAllLoading && <Spinner variant="admin-primary-thin" />}
                    <button
                      onClick={() => setRewriteAllOpen(false)}
                      disabled={rewriteAllLoading}
                      className="px-3 py-1 text-xs rounded border border-admin_bordered text-admin_text_secondary hover:bg-admin_neutral_1 transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleRewriteAll}
                      disabled={rewriteAllLoading || !rewriteAllDirections.trim()}
                      className="px-3 py-1 text-xs rounded bg-admin_primary text-white hover:bg-admin_primary/90 transition disabled:opacity-50"
                    >
                      {rewriteAllLoading ? "Rewriting…" : "Rewrite All"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
})

export default ObjectEditor
